import { useCallback, useEffect, useState } from "react";

import { api, ApiClientError } from "../api.js";
import { DataTable } from "../components/data-table.js";
import { MetricCard } from "../components/metric-card.js";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  PeriodSelector
} from "../components/state.js";
import { TrendChart } from "../components/trend-chart.js";
import {
  formatBytes,
  formatDate,
  formatNumber,
  formatPercent,
  formatSpeed,
  shortId
} from "../format.js";
import type { Overview, QueryPeriod } from "../types.js";

export function OverviewPage({
  onUnauthorized,
  onOpenError
}: {
  onUnauthorized(): void;
  onOpenError(fingerprint: string): void;
}) {
  const [period, setPeriod] = useState<QueryPeriod>({ period: "24h" });
  const [version, setVersion] = useState("");
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      setError("");
      setData(await api.overview(period, version));
    } catch (caught) {
      if (caught instanceof ApiClientError && caught.status === 401) {
        onUnauthorized();
        return;
      }
      setError(caught instanceof Error ? caught.message : String(caught));
    }
  }, [onUnauthorized, period, version]);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), 15_000);
    return () => window.clearInterval(timer);
  }, [load]);

  if (!data && !error) {
    return <LoadingState label="СИНХРОНИЗАЦИЯ СВОДКИ" />;
  }
  if (!data && error) {
    return <ErrorState message={error} onRetry={() => void load()} />;
  }
  if (!data) {
    return null;
  }

  const types = data.period.installation_types;
  const knownTypes = types.licensed + types.pirated;
  const licensedShare = knownTypes === 0 ? 0 : (types.licensed / knownTypes) * 100;
  const trendPointLabels = data.trend.map((point) =>
    new Date(point.bucket_start).toLocaleString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/Moscow"
    })
  );
  const trendStartLabel = trendPointLabels.at(0) ?? "";
  const trendEndLabel = trendPointLabels.at(-1) ?? "";
  const stability = data.stability;
  let stabilityValue: string;
  let stabilityTone: "good" | "warn" | "bad";
  let stabilityNote: string;
  if (stability.status === "stable") {
    stabilityValue = "СТАБИЛЬНО";
    stabilityTone = "good";
    stabilityNote = "30 МИН · НЕТ ОТКРЫТЫХ ИНЦИДЕНТОВ";
  } else {
    stabilityValue = stability.status === "critical" ? "КРИТИЧНО" : "ВНИМАНИЕ";
    stabilityTone = stability.status === "critical" ? "bad" : "warn";
    const affected = stability.primary_incident?.affected_installations;
    stabilityNote = `30 МИН · ${
      stability.primary_incident?.title ?? "ОТКРЫТЫЙ ИНЦИДЕНТ"
    }${affected === null || affected === undefined ? "" : ` · ${formatNumber(affected)} ЗАТРОНУТО`}`;
  }
  return (
    <div className="page-stack">
      <section className="toolbar">
        <PeriodSelector value={period} onChange={setPeriod} />
        <label className="field field--compact">
          <span>ВЕРСИЯ</span>
          <input
            value={version}
            onChange={(event) => setVersion(event.target.value)}
            placeholder="ВСЕ"
          />
        </label>
        <div className="toolbar__stamp">
          ОБНОВЛЕНО {formatDate(data.live.sampled_at)}
        </div>
      </section>

      <section className="metric-grid metric-grid--six">
        <MetricCard
          label="ЛАУНЧЕР ОНЛАЙН"
          value={formatNumber(data.live.online)}
          note="HEARTBEAT ≤ 150 СЕК"
          tone="good"
          marker="LIVE"
        />
        <MetricCard
          label="ИГРА ЗАПУЩЕНА"
          value={formatNumber(data.live.game_running)}
          note={`${formatPercent(
            data.live.online
              ? (data.live.game_running / data.live.online) * 100
              : 0
          )} ОТ ОНЛАЙНА`}
          tone="good"
        />
        <MetricCard
          label="СКАЧИВАЮТ"
          value={formatNumber(data.live.downloading)}
          note="ТЕКУЩЕЕ СОСТОЯНИЕ"
          tone="neutral"
        />
        <MetricCard
          label="АКТИВНЫЕ КЛИЕНТЫ"
          value={formatNumber(data.period.active_installations)}
          note={data.range.key.toUpperCase()}
        />
        <MetricCard
          label="ERROR RATE"
          value={formatPercent(data.period.error_rate)}
          note={`${formatNumber(data.period.affected_installations)} ЗАТРОНУТО`}
          tone={
            data.period.error_rate > 10
              ? "bad"
              : data.period.error_rate > 3
                ? "warn"
                : "good"
          }
        />
        <MetricCard
          label="СТАБИЛЬНОСТЬ ЛАУНЧЕРА"
          value={stabilityValue}
          note={stabilityNote}
          tone={stabilityTone}
          marker="CURRENT"
        />
      </section>

      <section className="dashboard-grid">
        <article className="panel panel--wide">
          <header className="panel__header">
            <div>
              <h2>ПИКОВЫЙ ОНЛАЙН ЛАУНЧЕРА</h2>
            </div>
            <span className="panel__meta">
              ЧАСОВ С ДАННЫМИ: {data.trend.length}
            </span>
          </header>
          {data.trend.length ? (
            <TrendChart
              values={data.trend.map((point) => point.online_peak)}
              label="ПИК ОНЛАЙНА"
              pointLabels={trendPointLabels}
              rangeStartLabel={trendStartLabel}
              rangeEndLabel={trendEndLabel}
            />
          ) : (
            <EmptyState>
              ГРАФИК ПОЯВИТСЯ ПОСЛЕ ПЕРВОЙ АГРЕГАЦИИ
            </EmptyState>
          )}
        </article>

        <article className="panel">
          <header className="panel__header">
            <div>
              <h2>СТАТУС ЛИЦЕНЗИИ</h2>
            </div>
          </header>
          <div className="type-split">
            <div
              className="type-split__licensed"
              style={{ width: `${licensedShare}%` }}
            />
            <div
              className="type-split__pirated"
              style={{ width: `${100 - licensedShare}%` }}
            />
          </div>
          <div className="distribution-list">
            <div>
              <span className="legend legend--licensed" />
              <span>ЛИЦЕНЗИЯ</span>
              <strong>{formatNumber(types.licensed)}</strong>
            </div>
            <div>
              <span className="legend legend--pirated" />
              <span>ПИРАТКА</span>
              <strong>{formatNumber(types.pirated)}</strong>
            </div>
            <div>
              <span className="legend legend--unknown" />
              <span>НЕИЗВЕСТНО</span>
              <strong>{formatNumber(types.unknown)}</strong>
            </div>
          </div>
          <div className="panel__footnote">
            ДОЛЯ ЛИЦЕНЗИЙ БЕЗ UNKNOWN: {formatPercent(licensedShare)}
          </div>
        </article>

        <article className="panel">
          <header className="panel__header">
            <div>
              <h2>ЗАГРУЗКИ</h2>
            </div>
          </header>
          <div className="summary-matrix">
            <div>
              <span>ДАННЫЕ</span>
              <strong>{formatBytes(data.downloads.bytes)}</strong>
            </div>
            <div>
              <span>ФАЙЛЫ</span>
              <strong>{formatNumber(data.downloads.files)}</strong>
            </div>
            <div>
              <span>СРЕДНЯЯ</span>
              <strong>{formatSpeed(data.downloads.mean_speed_bps)}</strong>
            </div>
            <div>
              <span>МЕДИАНА</span>
              <strong>{formatSpeed(data.downloads.median_speed_bps)}</strong>
            </div>
          </div>
          <div className="result-line">
            <span className="text-good">{data.downloads.success} OK</span>
            <span className="text-bad">{data.downloads.failed} FAILED</span>
            <span>{data.downloads.cancelled} CANCELLED</span>
            <span>{data.downloads.interrupted} INTERRUPTED</span>
          </div>
        </article>

        <article className="panel panel--wide">
          <header className="panel__header">
            <div>
              <h2>ПОСЛЕДНИЕ ГРУППЫ ОШИБОК</h2>
            </div>
            <span className="panel__meta">
              {formatNumber(data.period.error_events)} СОБЫТИЙ
            </span>
          </header>
          {data.recent_errors.length ? (
            <DataTable
              headings={[
                "КОД / ОПЕРАЦИЯ",
                "ВЕРСИЯ",
                "СЛУЧАИ",
                "ЗАТРОНУТЫЕ ЛАУНЧЕРЫ",
                "ПОСЛЕДНЯЯ"
              ]}
            >
              {data.recent_errors.map((item) => (
                <tr
                  key={item.fingerprint}
                  className="clickable-row"
                  onClick={() => onOpenError(item.fingerprint)}
                >
                  <td>
                    <strong>{item.code || item.exception_type}</strong>
                    <small>{item.operation}</small>
                  </td>
                  <td className="mono">{item.launcher_version}</td>
                  <td className="mono">{item.occurrence_count}</td>
                  <td className="mono">{item.affected_installations}</td>
                  <td className="mono">
                    {formatDate(item.last_seen_at)}
                    <small>{shortId(item.fingerprint)}</small>
                  </td>
                </tr>
              ))}
            </DataTable>
          ) : (
            <EmptyState>ОШИБОК В ВЫБРАННОМ ПЕРИОДЕ НЕТ</EmptyState>
          )}
        </article>
      </section>
    </div>
  );
}
